export class Duration {
    ms: number;

    private constructor(ms: number) {
        this.ms = ms;
    }

    public static fromSeconds(seconds: number): Duration {
        return new Duration(seconds * 1000);
    }

    public static fromMillis(ms: number): Duration {
        return new Duration(ms);
    }

    public static fromRaw(rawData: any) {
        if (rawData.ms !== undefined) {
            return Duration.fromMillis(rawData.ms);
        } else if (rawData.seconds !== undefined) {
            return Duration.fromSeconds(rawData.seconds);
        } else {
            throw new Error("Raw data doesn't contain either seconds or millis");
        }
    }

    public asMillis(): number {
        return this.ms;
    }

    public asDisplayString(): string {
        const seconds = this.ms / 1000;
        const minutes = seconds / 60;
        function prettyString(num: number): string {
            return num.toFixed(2).replace(".00", "");
        }
        if (this.ms === 0) {
            return "0s";
        }
        if (this.ms < 1000) {
            return `${this.ms}ms`;
        } else if (this.ms < 1000 * 60) {
            return `${prettyString(seconds)}s`
        } else {
            return `${prettyString(minutes)}m`
        }
    }

    public greaterThan(other: Duration): boolean {
        return this.ms > other.ms;
    }

    public equals(other: Duration): boolean {
        return this.ms === other.ms;
    }
}

export class DurationRange {
    from: Duration;
    to: Duration;

    public constructor(from: Duration, to: Duration) {
        if (from.asMillis > to.asMillis) {
            throw new Error("`from` duration is longer than `to` duration.");
        }
        this.from = from;
        this.to = to;
    }

    public static fromRaw(rawData: any): DurationRange {
        return new DurationRange(
            Duration.fromRaw(rawData.from),
            rawData.to !== undefined ? Duration.fromRaw(rawData.to) : rawData.from,
        )
    }

    public static fromMillis(from: number, to: number): DurationRange {
        return new DurationRange(
            Duration.fromMillis(from),
            Duration.fromMillis(to),
        )
    }

    public static ZERO(): DurationRange {
        return new DurationRange(
            Duration.fromMillis(0),
            Duration.fromMillis(0),
        )
    }

    public getRandom(): Duration {
        const min = this.from.asMillis();
        const max = this.to.asMillis();
        const millis = Math.random() * (max - min) + min;
        return Duration.fromMillis(millis);
    }

    public equals(other: DurationRange): boolean {
        return this.from.equals(other.from) && this.to.equals(other.to);
    }
}
